/**
 * PuzzleList — scrollable table of all puzzles in puzzles_admin.
 *
 * Columns: Solution (text), Status (colored dot), Date, Sample (checkbox).
 * Features: click-to-sort column headers, canned filter buttons,
 * click-to-select, double-click date to edit, click checkbox to toggle sample.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import type { PuzzleJSON } from '../../types';
import styles from './PuzzleList.module.css';

// ─── Types ───────────────────────────────────────────────

export type PuzzleStatus = 'scheduled' | 'ready' | 'core' | 'stub';

export interface PuzzleListItem {
  id: string;
  solution: string;
  status: PuzzleStatus;
  date: string | null;
  isSample: boolean;
  puzzleJson: PuzzleJSON | null;
}

type SortField = 'solution' | 'status' | 'date' | 'isSample';
type SortDir = 'asc' | 'desc';
type FilterKey = 'all' | 'scheduled' | 'ready' | 'core' | 'stub';

export interface PuzzleListHandle {
  compressDates: () => Promise<void>;
}

interface PuzzleListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Incremented by parent after saves to trigger a refresh */
  refreshKey: number;
  /** Called when the sample checkbox is toggled for a puzzle */
  onSampleToggled?: (id: string, isSample: boolean) => void;
  /** Called once after initial fetch with the first displayed item's ID */
  onFirstItemReady?: (id: string) => void;
}

// ─── Supabase ────────────────────────────────────────────

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function sbFetch(path: string, options?: RequestInit) {
  return fetch(`${supabaseUrl}${path}`, {
    ...options,
    headers: {
      'apikey': supabaseKey,
      'Authorization': `Bearer ${supabaseKey}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal',
      ...(options?.headers ?? {}),
    },
  });
}

// ─── Status computation ──────────────────────────────────

function computeStatus(json: PuzzleJSON | null, date: string | null, hasPuzzleText?: boolean): PuzzleStatus {
  if (!json) {
    // Old text-format puzzles: if puzzle_text exists, treat as complete (core at minimum)
    if (hasPuzzleText) return date ? 'scheduled' : 'ready';
    return 'stub';
  }

  const { solution, columns } = json;
  if (!solution?.primary) return 'stub';

  // All 24 clues must be non-empty
  const allClues = columns.every(col =>
    col.pairs.every(pair => pair.clues[0].trim() !== '' && pair.clues[1].trim() !== '')
  );
  if (!allClues) return 'stub';

  // Check all 4 category descriptions
  const allCategories = columns.every(col => col.category?.trim() !== '');
  if (!allCategories) return 'core';

  // Fully complete — scheduled or ready
  if (date) return 'scheduled';
  return 'ready';
}

const STATUS_ORDER: Record<PuzzleStatus, number> = { scheduled: 0, ready: 1, core: 2, stub: 3 };
const STATUS_COLOR: Record<PuzzleStatus, string> = {
  scheduled: '#387c47',
  ready: '#e6b832',
  core: '#d26f28',
  stub: '#7a6088',
};
const STATUS_LABEL: Record<PuzzleStatus, string> = {
  scheduled: 'Scheduled',
  ready: 'Ready',
  core: 'Core',
  stub: 'Stub',
};

/** Format a Date as YYYY-MM-DD in local time (avoids UTC drift from toISOString) */
function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Filter button colors (muted status colors + sample) ─

const FILTER_BG: Record<FilterKey, string> = {
  all: '#4a4440',
  scheduled: '#2e6b3a',
  ready: '#9a7e30',
  core: '#9a5424',
  stub: '#5c4870',
};

const FILTER_BG_ACTIVE: Record<FilterKey, string> = {
  all: '#5a5450',
  scheduled: '#38884a',
  ready: '#b8983e',
  core: '#c06828',
  stub: '#7e5c98',
};

// ─── Component ───────────────────────────────────────────

const PuzzleList = forwardRef<PuzzleListHandle, PuzzleListProps>(({ selectedId, onSelect, refreshKey, onSampleToggled, onFirstItemReady }, ref) => {
  const [items, setItems] = useState<PuzzleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set());
  const [showPast, setShowPast] = useState(false);
  const [sampleOnly, setSampleOnly] = useState(false);
  const [editingDate, setEditingDate] = useState<string | null>(null); // id being edited
  const [dateValue, setDateValue] = useState('');

  // ─── Drag-and-drop state for sample reordering ──────────
  const [sampleOrder, setSampleOrder] = useState<string[]>([]); // manual order of sample IDs
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Fetch all puzzles
  const fetchPuzzles = useCallback(async () => {
    try {
      const res = await sbFetch(
        '/rest/v1/puzzles_admin?select=id,date,is_sample,puzzle_json,name,puzzle_text&order=date.desc.nullslast&limit=200'
      );
      if (!res.ok) return;
      const rows: Array<{ id: string; date: string | null; is_sample: boolean; puzzle_json: PuzzleJSON | null; name: string | null; puzzle_text: string | null }> = await res.json();
      setItems(rows.map(r => ({
        id: r.id,
        solution: r.puzzle_json?.solution?.primary ?? r.name ?? '',
        status: computeStatus(r.puzzle_json, r.date, !!r.puzzle_text),
        date: r.date,
        isSample: r.is_sample ?? false,
        puzzleJson: r.puzzle_json,
      })));
    } catch {
      // silent
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPuzzles(); }, [fetchPuzzles, refreshKey]);

  // Initialize sample order from date-sorted samples (only on first load / refresh)
  useEffect(() => {
    const samples = items
      .filter(i => i.isSample && i.date)
      .sort((a, b) => (a.date! < b.date! ? -1 : a.date! > b.date! ? 1 : 0))
      .map(i => i.id);
    // Also include undated samples at the end
    const undated = items.filter(i => i.isSample && !i.date).map(i => i.id);
    setSampleOrder([...samples, ...undated]);
  }, [items.length]); // only re-derive when count changes (not on every optimistic update)

  // Toggle sort
  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'date' ? 'desc' : 'asc');
    }
  }, [sortField]);

  // Toggle filter — 'all' clears everything
  const toggleFilter = useCallback((key: FilterKey) => {
    if (key === 'all') {
      setActiveFilters(new Set());
      return;
    }
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const today = useMemo(() => localISO(new Date()), []);

  // Filter
  const filtered = useMemo(() => {
    return items.filter(item => {
      // Checkbox: "Sample Games Only" — exclude non-sample puzzles
      if (sampleOnly && !item.isSample) return false;
      // Checkbox: "Show Past Games" — if unchecked, hide past-dated puzzles
      if (!showPast && item.date && item.date < today) return false;
      // Status filter buttons
      if (activeFilters.size > 0) {
        let matchesStatus = false;
        for (const f of activeFilters) {
          if (f === 'scheduled' && item.status === 'scheduled') { matchesStatus = true; break; }
          if (f === 'ready' && item.status === 'ready') { matchesStatus = true; break; }
          if (f === 'core' && item.status === 'core') { matchesStatus = true; break; }
          if (f === 'stub' && item.status === 'stub') { matchesStatus = true; break; }
        }
        if (!matchesStatus) return false;
      }
      return true;
    });
  }, [items, activeFilters, showPast, sampleOnly, today]);

  // Build a lookup from sampleOrder for sort comparisons
  const sampleOrderIndex = useMemo(() => {
    const map = new Map<string, number>();
    sampleOrder.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [sampleOrder]);

  // Sort — when sorting by date, samples sort by sampleOrder position (drag order)
  // so that drag-and-drop reordering is immediately visible
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'solution':
          cmp = a.solution.localeCompare(b.solution);
          break;
        case 'status':
          cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
          break;
        case 'date': {
          // Both samples → sort by drag order
          const aIdx = sampleOrderIndex.get(a.id);
          const bIdx = sampleOrderIndex.get(b.id);
          if (a.isSample && b.isSample && aIdx !== undefined && bIdx !== undefined) {
            cmp = aIdx - bIdx;
          } else if (a.isSample && !b.isSample) {
            // Samples before non-samples when sorting asc (earliest first)
            cmp = -1;
          } else if (!a.isSample && b.isSample) {
            cmp = 1;
          } else {
            // Both non-samples → sort by date
            if (!a.date && !b.date) cmp = 0;
            else if (!a.date) cmp = 1;
            else if (!b.date) cmp = -1;
            else cmp = a.date.localeCompare(b.date);
          }
          break;
        }
        case 'isSample':
          cmp = (a.isSample ? 1 : 0) - (b.isSample ? 1 : 0);
          break;
      }
      return cmp * dir;
    });
    return arr;
  }, [filtered, sortField, sortDir, sampleOrderIndex]);

  // Notify parent of first displayed item for auto-selection
  const firstItemNotifiedRef = useRef(false);
  useEffect(() => {
    if (!firstItemNotifiedRef.current && sorted.length > 0 && onFirstItemReady) {
      firstItemNotifiedRef.current = true;
      onFirstItemReady(sorted[0].id);
    }
  }, [sorted, onFirstItemReady]);

  // Compute next available date from local items (stays fresh after optimistic updates)
  const getNextVacancy = useCallback((takenSet?: Set<string>, startDate?: string) => {
    const taken = takenSet ?? new Set(items.filter(p => p.date).map(p => p.date!));
    const d = startDate ? new Date(startDate + 'T00:00:00') : new Date();
    for (let i = 0; i < 365; i++) {
      const iso = localISO(d);
      if (!taken.has(iso)) return iso;
      d.setDate(d.getDate() + 1);
    }
    return null;
  }, [items]);

  const localNextVacancy = useMemo(() => getNextVacancy(), [getNextVacancy]);

  // Inline date editing
  const handleDateClick = useCallback((id: string, currentDate: string | null) => {
    setEditingDate(id);
    setDateValue(currentDate ?? localNextVacancy ?? '');
  }, [localNextVacancy]);

  const commitDate = useCallback(async (id: string) => {
    const newDate = dateValue || null;
    // Duplicate check
    if (newDate) {
      const conflict = items.find(p => p.id !== id && p.date === newDate);
      if (conflict) {
        alert(`Date ${newDate} is already used by "${conflict.solution}".`);
        return;
      }
    }
    setEditingDate(null);
    // Optimistic update
    setItems(prev => prev.map(p =>
      p.id === id ? { ...p, date: newDate, status: computeStatus(p.puzzleJson, newDate) } : p
    ));
    await sbFetch(`/rest/v1/puzzles_admin?id=eq.${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ date: newDate }),
    });
  }, [dateValue, items]);

  // Sample toggle — auto-assign earliest date when toggling ON
  const toggleSample = useCallback(async (id: string, current: boolean) => {
    const next = !current;
    if (next) {
      // Toggling ON — auto-assign earliest available date from today
      const item = items.find(p => p.id === id);
      const needsDate = !item?.date;
      const assignedDate = needsDate ? getNextVacancy() : null;

      setItems(prev => prev.map(p => {
        if (p.id !== id) return p;
        const newDate = needsDate && assignedDate ? assignedDate : p.date;
        return { ...p, isSample: true, date: newDate, status: computeStatus(p.puzzleJson, newDate) };
      }));
      setSampleOrder(prev => [...prev, id]);
      onSampleToggled?.(id, true);

      const patch: Record<string, unknown> = { is_sample: true };
      if (needsDate && assignedDate) patch.date = assignedDate;
      await sbFetch(`/rest/v1/puzzles_admin?id=eq.${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    } else {
      // Toggling OFF
      setItems(prev => prev.map(p => p.id === id ? { ...p, isSample: false } : p));
      setSampleOrder(prev => prev.filter(sid => sid !== id));
      onSampleToggled?.(id, false);
      await sbFetch(`/rest/v1/puzzles_admin?id=eq.${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_sample: false }),
      });
    }
  }, [onSampleToggled, items, getNextVacancy]);

  // Compute sample ordinals from manual sampleOrder
  const sampleOrdinals = useMemo(() => {
    const map = new Map<string, number>();
    const sampleSet = new Set(items.filter(i => i.isSample).map(i => i.id));
    let ordinal = 1;
    for (const id of sampleOrder) {
      if (sampleSet.has(id)) {
        map.set(id, ordinal++);
      }
    }
    // Any samples not in sampleOrder (shouldn't happen, but safety)
    for (const id of sampleSet) {
      if (!map.has(id)) map.set(id, ordinal++);
    }
    return map;
  }, [items, sampleOrder]);

  // ─── Drag-and-drop handlers for sample reordering ────────
  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    if (!items.find(i => i.id === id)?.isSample) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  }, [items]);

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    if (!dragId || !items.find(i => i.id === id)?.isSample) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(id);
  }, [dragId, items]);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setDragOverId(null); return; }

    // 1. Compute new sample order
    const newOrder = [...sampleOrder];
    const fromIdx = newOrder.indexOf(dragId);
    const toIdx = newOrder.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) { setDragId(null); setDragOverId(null); return; }
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragId);
    setSampleOrder(newOrder);

    // 2. Collect sorted dates currently held by dated samples
    const sampleSet = new Set(items.filter(i => i.isSample).map(i => i.id));
    const datedSamples = newOrder.filter(id => sampleSet.has(id) && items.find(i => i.id === id)?.date);
    const sortedDates = datedSamples
      .map(id => items.find(i => i.id === id)!.date!)
      .sort();

    // 3. Redistribute: assign the sorted dates to samples in new drag order
    const dateUpdates = new Map<string, string>();
    datedSamples.forEach((id, idx) => {
      const currentDate = items.find(i => i.id === id)!.date!;
      if (currentDate !== sortedDates[idx]) {
        dateUpdates.set(id, sortedDates[idx]);
      }
    });

    if (dateUpdates.size > 0) {
      // Optimistic local update
      setItems(prev => prev.map(p => {
        const newDate = dateUpdates.get(p.id);
        if (!newDate) return p;
        return { ...p, date: newDate, status: computeStatus(p.puzzleJson, newDate) };
      }));

      // Persist to Supabase — clear then reassign to avoid unique date constraint
      const ids = [...dateUpdates.keys()];
      Promise.all(ids.map(id =>
        sbFetch(`/rest/v1/puzzles_admin?id=eq.${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ date: null }),
        })
      )).then(async () => {
        for (const [id, date] of dateUpdates) {
          await sbFetch(`/rest/v1/puzzles_admin?id=eq.${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ date }),
          });
        }
      });
    }

    setDragId(null);
    setDragOverId(null);
  }, [dragId, sampleOrder, items]);

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setDragOverId(null);
  }, []);

  // ─── Compress Dates ─────────────────────────────────────
  // Reassigns consecutive dates starting from today for ALL scheduled puzzles.
  // Order: samples first (in drag order), then non-samples (by current date).
  const compressDates = useCallback(async () => {
    // 1. Gather ALL puzzles that have a date (including past-dated ones)
    const scheduled = items.filter(i => i.date);
    if (scheduled.length === 0) return;

    // 2. Split into samples (in drag order) and non-samples (by current date)
    const sampleSet = new Set(items.filter(i => i.isSample).map(i => i.id));
    const scheduledIdSet = new Set(scheduled.map(i => i.id));

    // Samples in sampleOrder that have a date
    const orderedSamples = sampleOrder
      .filter(id => sampleSet.has(id) && scheduledIdSet.has(id));
    // Non-samples sorted by their current date
    const nonSamples = scheduled
      .filter(i => !sampleSet.has(i.id))
      .sort((a, b) => a.date!.localeCompare(b.date!))
      .map(i => i.id);

    const ordered = [...orderedSamples, ...nonSamples];

    // 3. Assign consecutive dates starting from today (local time)
    const d = new Date();
    const updates: Array<{ id: string; date: string }> = [];
    for (const id of ordered) {
      const iso = localISO(d);
      const item = items.find(i => i.id === id)!;
      if (item.date !== iso) {
        updates.push({ id, date: iso });
      }
      d.setDate(d.getDate() + 1);
    }

    if (updates.length === 0) return;

    // 4. Optimistic local update
    const updateMap = new Map(updates.map(u => [u.id, u.date]));
    setItems(prev => prev.map(p => {
      const newDate = updateMap.get(p.id);
      if (!newDate) return p;
      return { ...p, date: newDate, status: computeStatus(p.puzzleJson, newDate) };
    }));

    // 5. Persist to Supabase — clear dates first to avoid unique constraint conflicts,
    //    then assign new dates sequentially
    const idsToUpdate = updates.map(u => u.id);
    // Clear all affected dates in one batch
    await Promise.all(idsToUpdate.map(id =>
      sbFetch(`/rest/v1/puzzles_admin?id=eq.${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ date: null }),
      })
    ));
    // Now assign new dates (sequential to avoid any remaining conflicts)
    for (const u of updates) {
      await sbFetch(`/rest/v1/puzzles_admin?id=eq.${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ date: u.date }),
      });
    }
  }, [items, sampleOrder]);

  // Expose compressDates to parent via ref
  useImperativeHandle(ref, () => ({ compressDates }), [compressDates]);

  // Sort arrow indicator
  const sortArrow = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const filterButtons: Array<{ key: FilterKey; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'ready', label: 'Ready' },
    { key: 'core', label: 'Core' },
    { key: 'stub', label: 'Stub' },
  ];

  return (
    <div className={styles.listPanel}>
      <span className={styles.regionLabel}>Puzzle List</span>
      {/* Status filter buttons + checkboxes */}
      <div className={styles.filterBar}>
        <div className={styles.filterBtnRow}>
          {filterButtons.map(fb => {
            const isAll = fb.key === 'all';
            const isActive = isAll ? activeFilters.size === 0 : activeFilters.has(fb.key);
            return (
              <button
                key={fb.key}
                className={styles.filterBtn}
                style={{
                  background: isActive ? FILTER_BG_ACTIVE[fb.key] : FILTER_BG[fb.key],
                  opacity: isActive ? 1 : 0.6,
                  border: isActive ? '1px solid #f7e5c5' : '1px solid transparent',
                }}
                onClick={() => toggleFilter(fb.key)}
              >
                {fb.label}
              </button>
            );
          })}
        </div>
        <div className={styles.filterCheckRow}>
          <label className={styles.filterCheck}>
            <input
              type="checkbox"
              checked={showPast}
              onChange={() => setShowPast(v => !v)}
            />
            Show Past
          </label>
          <label className={styles.filterCheck}>
            <input
              type="checkbox"
              checked={sampleOnly}
              onChange={() => setSampleOnly(v => !v)}
            />
            Samples Only
          </label>
          <span className={styles.filterCount}>{sorted.length} result{sorted.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thDate} onClick={() => handleSort('date')}>
                Date{sortArrow('date')}
              </th>
              <th className={styles.thSample} onClick={() => handleSort('isSample')}>
                S{sortArrow('isSample')}
              </th>
              <th className={styles.thOrdinal}>#</th>
              <th className={styles.thStatus} onClick={() => handleSort('status')}>
                {sortArrow('status') || '●'}
              </th>
              <th className={styles.thSolution} onClick={() => handleSort('solution')}>
                Solution{sortArrow('solution')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className={styles.empty}>Loading...</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={5} className={styles.empty}>No puzzles</td></tr>
            )}
            {sorted.map(item => (
              <tr
                key={item.id}
                className={`${styles.row} ${item.id === selectedId ? styles.rowSelected : ''} ${item.isSample ? styles.rowDraggable : ''} ${dragId === item.id ? styles.rowDragging : ''} ${dragOverId === item.id && dragId !== item.id ? styles.rowDragOver : ''}`}
                onClick={() => onSelect(item.id)}
                draggable={item.isSample}
                onDragStart={e => handleDragStart(e, item.id)}
                onDragOver={e => handleDragOver(e, item.id)}
                onDrop={e => handleDrop(e, item.id)}
                onDragEnd={handleDragEnd}
              >
                <td
                  className={`${styles.tdDate} ${styles.tdDateHover}`}
                  onClick={e => { e.stopPropagation(); handleDateClick(item.id, item.date); }}
                >
                  {editingDate === item.id ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <input
                        type="text"
                        className={styles.dateInput}
                        value={dateValue}
                        onChange={e => setDateValue(e.target.value)}
                        onBlur={() => setEditingDate(null)}
                        onKeyDown={e => { if (e.key === 'Enter') commitDate(item.id); if (e.key === 'Escape') setEditingDate(null); }}
                        autoFocus
                        onClick={e => e.stopPropagation()}
                        placeholder="YYYY-MM-DD"
                      />
                      <button
                        className={styles.dateConfirm}
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => { e.stopPropagation(); commitDate(item.id); }}
                        title="Accept date (or press Enter)"
                      >✓</button>
                    </span>
                  ) : (
                    item.date ? (
                      <span className={item.date < today ? styles.datePast : ''}>
                        {item.date}
                      </span>
                    ) : (
                      <span style={{ opacity: 0.35 }}>{'---'}</span>
                    )
                  )}
                </td>
                <td className={styles.tdSample}>
                  <input
                    type="checkbox"
                    checked={item.isSample}
                    onChange={() => toggleSample(item.id, item.isSample)}
                    onClick={e => e.stopPropagation()}
                    className={styles.sampleCheck}
                  />
                </td>
                <td className={styles.tdOrdinal}>
                  {sampleOrdinals.get(item.id) ?? ''}
                </td>
                <td className={styles.tdStatus}>
                  <span
                    className={styles.statusDot}
                    style={{ background: STATUS_COLOR[item.status] }}
                    title={STATUS_LABEL[item.status]}
                  />
                </td>
                <td
                  className={styles.tdSolution}
                  title={item.solution.toUpperCase() || '(no solution)'}
                >
                  {item.solution.toUpperCase() || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

PuzzleList.displayName = 'PuzzleList';

export default React.memo(PuzzleList);
